import type { UpdateOrderStatusParams } from '@/entities/order/model'
import { Button, Radio, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMemo, useState } from 'react'
import { getOrderType } from '@/entities/order/lib'
import { useOrderStatusMutation, useRawOrdersQuery } from '@/entities/order/model'
import { deriveStatusOptions, useCollectionCodeEnabled, useStateConfigQuery, useTmInitialsEnabled } from '@/entities/state/model'
import { CRATE_SCAN_PENDING_MESSAGE, notifyGroupScanIncomplete, notifyOrderScanIncomplete, useCrateScanGate, useGroupTransitionGuard, useOrderGroupMembers, useOrderTransitionGuard, usePoaGate } from '@/features/order/lib'
import { useCollectionCodeModal } from '@/features/order/ui/collection-code-modal'
import { useTmInitialsStore } from '@/features/order/ui/initials-capture-modal'
import { useNoBarcodeCompletionConfirm } from '@/features/order/ui/no-barcode-confirmation'

interface OrderStatusUpdateProps {
  orderId: string
  currentStatus: string
  lastUpdateTime?: string
  onSuccess?: (newStatus: string) => void
}

export function OrderStatusUpdate({
  orderId,
  currentStatus,
  lastUpdateTime,
  onSuccess,
}: OrderStatusUpdateProps) {
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus)
  const isDirty = selectedStatus !== currentStatus

  const { data: allOrders } = useRawOrdersQuery()
  const isCollectionCodeEnabled = useCollectionCodeEnabled()
  const isTmInitialsEnabled = useTmInitialsEnabled()
  const { data: stateConfig, isLoading: isLoadingStates } = useStateConfigQuery()
  const { mutateAsync: updateStatusMutation, isPending } = useOrderStatusMutation()
  const groupMemberOrderIds = useOrderGroupMembers(orderId)
  // BD-8186: the status drawer drives the mutation itself, so it must consult the same gate.
  const { isTransitionBlocked } = useGroupTransitionGuard(orderId)
  const { open: openCollectionCodeModal } = useCollectionCodeModal()
  const enqueueInitials = useTmInitialsStore(state => state.enqueue)
  const { shouldGateCompletion, openPoaChecks } = usePoaGate()
  const { isCrateScanPending } = useCrateScanGate()
  const { requiresConfirm, requestConfirm } = useNoBarcodeCompletionConfirm()
  const order = allOrders?.find(o => o.orderId === orderId)
  // BD-8130: the group moves together, so the BD-8589 confirmation covers every member.
  const loadedGroupMembers = allOrders?.filter(o => groupMemberOrderIds.includes(o.orderId)) ?? []
  const groupMemberOrders = loadedGroupMembers.length > 0 ? loadedGroupMembers : (order ? [order] : [])
  // BD-8644: a single order gets the same rule on its own path.
  const { isOrderTransitionBlocked } = useOrderTransitionGuard(order)
  const normalizedOrderType = getOrderType(order?.hsFields)?.trim().toLowerCase()
  const configuredEligibleOrderTypes = (stateConfig?.collectCodeEligibleOrderTypes ?? [])
    .map(type => type.trim().toLowerCase())
    .filter(Boolean)
  const isCollectionCodeEligibleOrder = configuredEligibleOrderTypes.includes(normalizedOrderType ?? '')

  const statusOptions = useMemo(() => {
    if (!stateConfig?.states)
      return []
    return deriveStatusOptions(stateConfig.states)
  }, [stateConfig])

  async function handleStatusUpdate() {
    if (!selectedStatus || !orderId)
      return

    // BD-8186: the group stays in Arrived until every order in it is fully scanned.
    if (isTransitionBlocked(selectedStatus)) {
      notifyGroupScanIncomplete()
      return
    }
    if (isOrderTransitionBlocked(selectedStatus)) {
      notifyOrderScanIncomplete()
      return
    }

    // BD-8589: completing without array_barcodes waits for the team member to confirm.
    if (order && requiresConfirm(groupMemberOrders, selectedStatus)) {
      requestConfirm(() => void applyStatusUpdate())
      return
    }

    await applyStatusUpdate()
  }

  async function applyStatusUpdate() {
    try {
      // BD-8245: the manual status route is gated too — a restricted order cannot
      // be forced to Completed from the dropdown without running the checks.
      if (selectedStatus === 'Completed' && shouldGateCompletion(order)) {
        // The same crate scan gate as the quick action: an order with crates still
        // unaccounted for must not reach the checks from the manual route either.
        if (isCrateScanPending(order)) {
          notifications.show({ message: CRATE_SCAN_PENDING_MESSAGE, color: 'red' })
          return
        }
        openPoaChecks(orderId)
        return
      }

      if (
        selectedStatus === 'Completed'
        && isCollectionCodeEnabled
        && isCollectionCodeEligibleOrder
      ) {
        openCollectionCodeModal({ orderId, orderIds: groupMemberOrderIds, nextStatus: selectedStatus, lastUpdateTime })
        return
      }

      // BD-8130: every status, Completed included, moves the whole group together.
      const updatePromises = groupMemberOrderIds.map((memberId) => {
        const updateParams: UpdateOrderStatusParams = {
          orderId: memberId,
          newStatus: selectedStatus,
          lastUpdateTime,
        }
        return updateStatusMutation(updateParams)
      })

      await Promise.all(updatePromises)

      // Initials are asked once for every order this completion applied to
      // (BD-8114 Scenarios 1-2; decoupled from completion + confetti).
      if (selectedStatus === 'Completed' && isTmInitialsEnabled) {
        enqueueInitials({ orderId, orderIds: groupMemberOrderIds, customerName: order?.customerName })
      }

      if (onSuccess) {
        onSuccess(selectedStatus)
      }
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  return (
    <Stack>
      {isLoadingStates ? (
        <Text>Loading available statuses...</Text>
      ) : statusOptions.length === 0 ? (
        <Text>No status transitions available</Text>
      ) : (
        <Radio.Group
          id="order-status-update-radio-group"
          value={selectedStatus}
          onChange={setSelectedStatus}
          name="orderStatus"
        >
          {statusOptions.map(option => (
            <Radio
              key={option.value}
              value={option.value}
              label={option.label}
              size="md"
              color="gray.8"
              py="0.5rem"
            />
          ))}
        </Radio.Group>
      )}

      <Button
        variant="filled"
        color="gray.8"
        fullWidth
        size="md"
        radius="md"
        onClick={handleStatusUpdate}
        loading={isPending}
        disabled={!selectedStatus || isPending || statusOptions.length === 0}
      >
        {isDirty ? 'Update' : 'Done'}
      </Button>
    </Stack>
  )
}
